import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Project, Company, NewObraData } from '../types';
import { useProjects } from '../hooks/useProjects';
import { useCompanies } from '../hooks/useCompanies';
import { useExpenses } from '../hooks/useExpenses';
import { DocumentPlusIcon } from '../components/icons/DocumentPlusIcon';
import { SearchIcon } from '../components/icons/SearchIcon';
import { GridViewIcon } from '../components/icons/GridViewIcon';
import { ListBulletIcon } from '../components/icons/ListBulletIcon';
import { ObraListItem } from '../components/ObraListItem';
import { AddObraModal } from '../components/AddObraModal';
import { Card } from '../components/ui/Card';

type ViewMode = 'grid' | 'list';

export const ObrasPage: React.FC = () => {
    const { projects, loading: projectsLoading, addProject } = useProjects();
    const { companies, loading: companiesLoading } = useCompanies();
    const { allExpenses, loading: expensesLoading } = useExpenses();
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState<ViewMode>(
        () => (localStorage.getItem('obrasViewMode') as ViewMode) || 'list'
    );
    const [isAddObraModalOpen, setIsAddObraModalOpen] = useState(false);
    
    // State for reorderable list
    const [displayedProjects, setDisplayedProjects] = useState<any[]>([]);

    // Refs for drag and drop
    const dragItem = useRef<string | null>(null);
    const dragOverItem = useRef<string | null>(null);

    useEffect(() => {
        try {
            localStorage.setItem('obrasViewMode', viewMode);
        } catch (error) {
            console.error("Failed to save view mode to localStorage.", error);
        }
    }, [viewMode]);

    const companiesMap = useMemo(() => new Map(companies.map(c => [c.id, c])), [companies]);

    // Reverted: Calculate totalCost on the client-side
    const projectsWithData = useMemo(() => {
        const costsByProject = allExpenses.reduce((acc, expense) => {
            acc[expense.projectId] = (acc[expense.projectId] || 0) + expense.amount;
            return acc;
        }, {} as { [key: string]: number });

        return projects.map(p => ({
            ...p,
            company: companiesMap.get(p.companyId),
            totalCost: costsByProject[p.id] || 0
        }));
    }, [projects, companiesMap, allExpenses]);

    // Effect to sort and set displayed projects based on saved order
    useEffect(() => {
        if (projectsWithData.length === 0 && !projectsLoading) return;
    
        const savedOrderStr = localStorage.getItem('lam-custos-project-order');
        const savedOrder = savedOrderStr ? JSON.parse(savedOrderStr) : null;
        
        let sortedProjects;
        if (savedOrder && Array.isArray(savedOrder)) {
            const projectMap = new Map(projectsWithData.map(p => [p.id, p]));
            sortedProjects = savedOrder
                .map((id: string) => projectMap.get(id))
                // FIX: The type predicate `p is Project` was incorrect because `p` is of a more specific type `Project & { company, totalCost }`. 
                // Using a generic and correct predicate `p is Exclude<typeof p, undefined>` correctly narrows the type without incorrect assertions.
                .filter((p): p is Exclude<typeof p, undefined> => p !== undefined);
            
            const orderedProjectIds = new Set(savedOrder);
            const newProjects = projectsWithData.filter(p => !orderedProjectIds.has(p.id));
            
            if (newProjects.length > 0) {
              newProjects.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
              sortedProjects.push(...newProjects);
            }
        } else {
            sortedProjects = [...projectsWithData].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        }
    
        setDisplayedProjects(sortedProjects);
    }, [projectsWithData, projectsLoading]);


    const filteredProjects = useMemo(() => {
        if (!searchTerm) {
            return displayedProjects;
        }

        const lowercasedSearch = searchTerm.toLowerCase();
        return displayedProjects.filter(p =>
            p.name.toLowerCase().includes(lowercasedSearch) ||
            p.serviceDescription.toLowerCase().includes(lowercasedSearch) ||
            p.personInCharge.toLowerCase().includes(lowercasedSearch) ||
            p.company?.name.toLowerCase().includes(lowercasedSearch) ||
            p.company?.address.city.toLowerCase().includes(lowercasedSearch)
        );
    }, [displayedProjects, searchTerm]);
    
    // Drag and Drop sort handler
    const handleSort = () => {
        if (!dragItem.current || !dragOverItem.current || dragItem.current === dragOverItem.current) {
            dragItem.current = null;
            dragOverItem.current = null;
            return;
        }

        const dragItemIndex = displayedProjects.findIndex(p => p.id === dragItem.current);
        const dragOverItemIndex = displayedProjects.findIndex(p => p.id === dragOverItem.current);

        if (dragItemIndex === -1 || dragOverItemIndex === -1) return;

        const reorderedProjects = [...displayedProjects];
        const [draggedItem] = reorderedProjects.splice(dragItemIndex, 1);
        reorderedProjects.splice(dragOverItemIndex, 0, draggedItem);
        
        setDisplayedProjects(reorderedProjects);
        localStorage.setItem('lam-custos-project-order', JSON.stringify(reorderedProjects.map(p => p.id)));

        dragItem.current = null;
        dragOverItem.current = null;
    };

    const handleAddObra = async (data: NewObraData) => {
        const company = companiesMap.get(data.companyId);
        if (!company) {
            alert('Empresa selecionada não encontrada.');
            return;
        }

        const newProject: Omit<Project, 'totalCost'> = {
            id: `proj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: `${company.name} - ${company.address.city}/${company.address.state}`,
            companyId: data.companyId,
            personInCharge: data.personInCharge,
            serviceDescription: data.serviceDescription,
            status: 'aberta',
            createdAt: new Date().toISOString(),
        };

        const result = await addProject(newProject as Project);
        if(result.success) {
            setIsAddObraModalOpen(false);
        } else {
            alert(`Falha ao criar a obra: ${result.message}`);
        }
    };
    
    const isLoading = projectsLoading || companiesLoading || expensesLoading;
    
    if (isLoading) {
        return <div className="text-center py-10">Carregando obras...</div>;
    }
    
    const renderContent = () => {
        if (filteredProjects.length > 0) {
            return (
                <div className={viewMode === 'grid' 
                    ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" 
                    : "space-y-4"
                }>
                    {filteredProjects.map(p => (
                        <ObraListItem
                            key={p.id}
                            project={p}
                            company={p.company}
                            viewMode={viewMode}
                            onClick={() => navigate(`/obras/${p.id}`)}
                            onDragStart={(id) => (dragItem.current = id)}
                            onDragEnter={(id) => (dragOverItem.current = id)}
                            onDragEnd={handleSort}
                        />
                    ))}
                </div>
            );
        }
        
        return (
            <Card className="bg-white dark:bg-dark-secondary">
                <div className="text-center py-12">
                    <p className="text-gray-500 dark:text-dark-text-secondary">Nenhuma obra encontrada.</p>
                    {searchTerm && <p className="text-sm text-gray-400 mt-2">Tente ajustar seus termos de busca.</p>}
                </div>
            </Card>
        );
    }

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary">Obras</h1>

                <div className="flex items-center gap-2 w-full md:w-auto">
                    <div className="relative flex-grow">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <SearchIcon className="text-xl text-gray-400" />
                        </div>
                        <Input
                            id="searchObras"
                            type="text"
                            placeholder="Pesquisar obras..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 w-full"
                            containerClassName="mb-0"
                        />
                    </div>
                    {/* View Mode Toggle */}
                    <div className="flex items-center bg-gray-200 dark:bg-dark-tertiary p-1 rounded-md">
                        <Button variant={viewMode === 'grid' ? 'primary' : 'ghost'} size="sm" onClick={() => setViewMode('grid')} className="p-1.5"><GridViewIcon className="text-xl"/></Button>
                        <Button variant={viewMode === 'list' ? 'primary' : 'ghost'} size="sm" onClick={() => setViewMode('list')} className="p-1.5"><ListBulletIcon className="text-xl"/></Button>
                    </div>
                    
                    <Button
                        variant="primary"
                        onClick={() => setIsAddObraModalOpen(true)}
                        leftIcon={<DocumentPlusIcon className="text-xl" />}
                    >
                        Nova Obra
                    </Button>
                </div>
            </div>
            
            {renderContent()}

            <AddObraModal
                isOpen={isAddObraModalOpen}
                onClose={() => setIsAddObraModalOpen(false)}
                onSubmit={handleAddObra}
            />
        </div>
    );
};
